import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { startTrialIfNeeded } from "@/utils/trialUtils";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import { sanitizeInput, isValidName, cleanName, isValidInput } from "@/utils/security";
import { validatePassword, getPasswordStrengthColor, getPasswordStrengthText } from "@/utils/passwordValidation";
import { Separator } from "@/components/ui/separator";

const SignUp = () => {
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [formErrors, setFormErrors] = useState<{
    email?: string;
    firstName?: string;
    lastName?: string;
    password?: string;
  }>({});
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: { warning: '', suggestions: [] },
    isStrong: false
  });
  const [emailSent, setEmailSent] = useState(false);
  
  useEffect(() => {
    document.title = "Sign Up | Lead Extract";
  }, []);

  useEffect(() => {
    setPasswordStrength(validatePassword(password));
  }, [password]);

  const validateForm = (formData: FormData): boolean => {
    let isValid = true;
    const errors: {
      email?: string;
      firstName?: string;
      lastName?: string;
      password?: string;
    } = {};
    
    const rawEmail = formData.get("email") as string;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(rawEmail)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }
    
    const rawFirstName = formData.get("firstName") as string;
    if (!rawFirstName || rawFirstName.trim().length < 2) {
      errors.firstName = "First name must be at least 2 characters";
      isValid = false;
    } else if (!isValidName(rawFirstName)) {
      errors.firstName = "First name contains invalid characters";
      isValid = false;
    }
    
    const rawLastName = formData.get("lastName") as string;
    if (!rawLastName || rawLastName.trim().length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
      isValid = false;
    } else if (!isValidName(rawLastName)) {
      errors.lastName = "Last name contains invalid characters";
      isValid = false;
    }
    
    if (!passwordStrength.isStrong) {
      const warningMessage = passwordStrength.feedback.warning || "Please use a stronger password";
      errors.password = warningMessage;
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData(e.currentTarget);
    if (!validateForm(formData)) {
      console.log("🔍 SIGNUP: Form validation failed, not proceeding with signup");
      return;
    }
    
    const email = sanitizeInput(formData.get("email") as string);
    const rawFirstName = formData.get("firstName") as string;
    const rawLastName = formData.get("lastName") as string;
    const firstName = cleanName(rawFirstName);
    const lastName = cleanName(rawLastName);
    
    console.log("🔍 SIGNUP: Starting signup process with:", { email, firstName, lastName });
    setLoading(true);
    
    try {
      console.log("🔍 SIGNUP: Calling signUp method with:", email);
      await signUp(email, password, firstName, lastName);
      
      setEmailSent(true);
      console.log("🔍 SIGNUP: Sign up successful, showing email verification screen");
      
      // Note: We don't navigate here because we're showing the email verification screen
    } catch (error: any) {
      console.error("🔍 SIGNUP ERROR: Sign up error:", error.message || error);
      console.error("🔍 SIGNUP ERROR DETAILS:", error);
      
      // Check if the error is related to an existing email
      if (error.message && (
        error.message.includes("already exists") || 
        error.message.includes("already registered") ||
        error.message.includes("A user with this email") ||
        error.message.includes("different email")
      )) {
        // Email already exists error
        setFormErrors(prev => ({
          ...prev,
          email: "An account with this email already exists. Please use a different email or try logging in."
        }));
        toast.error("This email is already registered. Please use a different email or try logging in.");
      } else {
        // Other errors
        toast.error(error.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      await signInWithGoogle();
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      toast.error(error.message || "An unexpected error occurred. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-white">
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <div className="flex justify-center mb-6">
            <Logo size="lg" showText={false} />
          </div>
          
          <h2 className="text-2xl text-center font-bold text-black">Check Your Email</h2>
          <p className="text-center text-gray-600">
            We've sent a confirmation link to your email address. Please check your inbox and click the link to activate your account.
          </p>
          
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Note:</span> If you don't see the email, please check your spam folder.
            </p>
          </div>
          
          <div className="pt-4 text-center">
            <Link to="/login" className="text-primary font-medium hover:underline">
              Return to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen flex items-center justify-center px-4 bg-white">
      <div className="w-full max-w-6xl flex flex-col md:flex-row rounded-xl overflow-hidden shadow-md">
        <div className="bg-black text-white p-8 md:p-12 md:w-1/2 flex flex-col justify-center border border-gray-200">
          <h1 className="text-3xl md:text-4xl font-bold mb-6">
            Identify the 98% of Website Visitors That Don't Convert
          </h1>
          
          <div className="space-y-6 mt-8">
            <div className="flex items-start space-x-3">
              <div className="h-6 w-6 rounded-full bg-primary flex-shrink-0 flex items-center justify-center text-white">✓</div>
              <div>
                <p className="font-medium">Get Instant Access</p>
                <p className="text-gray-300 text-sm">Start identifying website visitors today</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="h-6 w-6 rounded-full bg-primary flex-shrink-0 flex items-center justify-center text-white">✓</div>
              <div>
                <p className="font-medium">5-minute setup</p>
                <p className="text-gray-300 text-sm">Get leads instantly</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="h-6 w-6 rounded-full bg-primary flex-shrink-0 flex items-center justify-center text-white">✓</div>
              <div>
                <p className="font-medium">AI-powered insights</p>
                <p className="text-gray-300 text-sm">Uncover opportunities from your traffic</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-8 md:p-12 md:w-1/2 border border-gray-100">
          <div className="max-w-md mx-auto">
            <div className="flex justify-center mb-6">
              <Logo size="lg" showText={false} />
            </div>
            
            <h2 className="text-2xl text-center mb-6 font-bold">Unlock Your Website's Hidden Leads
          </h2>
            <p className="text-center text-gray-600 mb-8">Turn anonymous visitors into real opportunities</p>
            
            <Button 
              type="button" 
              variant="outline" 
              className="w-full border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-2 mb-6" 
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512" width="16" height="16">
                <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z" fill="#4285F4" />
              </svg>
              {googleLoading ? "Signing up..." : "Sign up with Google"}
            </Button>
            
            <div className="flex items-center w-full gap-4 my-4">
              <Separator className="flex-1" />
              <span className="text-sm text-gray-500">or</span>
              <Separator className="flex-1" />
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-firstname" className="text-black">First Name</Label>
                  <Input 
                    id="signup-firstname" 
                    name="firstName" 
                    type="text" 
                    required 
                    className={`bg-gray-50 ${formErrors.firstName ? 'border-red-500' : ''}`} 
                  />
                  {formErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-lastname" className="text-black">Last Name</Label>
                  <Input 
                    id="signup-lastname" 
                    name="lastName" 
                    type="text" 
                    required 
                    className={`bg-gray-50 ${formErrors.lastName ? 'border-red-500' : ''}`} 
                  />
                  {formErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-black">Email</Label>
                <Input 
                  id="signup-email" 
                  name="email" 
                  type="email" 
                  placeholder="name@company.com" 
                  required 
                  className={`bg-gray-50 ${formErrors.email ? 'border-red-500' : ''}`} 
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-password" className="text-black">Password</Label>
                <Input 
                  id="signup-password" 
                  name="password" 
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  className={`bg-gray-50 ${formErrors.password ? 'border-red-500' : ''}`} 
                />
                
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Password strength:</span>
                    <span className={password ? `font-semibold ${passwordStrength.score >= 3 ? 'text-green-600' : 'text-orange-600'}` : 'text-gray-400'}>
                      {password ? getPasswordStrengthText(passwordStrength.score) : 'None'}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getPasswordStrengthColor(passwordStrength.score)}`} 
                      style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                    />
                  </div>
                  
                  {password && (
                    <div className="mt-2 text-xs">
                      {passwordStrength.feedback.warning && (
                        <p className="text-orange-600 mt-1">{passwordStrength.feedback.warning}</p>
                      )}
                      {passwordStrength.feedback.suggestions.length > 0 && (
                        <ul className="text-gray-600 list-disc ml-4 mt-1">
                          {passwordStrength.feedback.suggestions.map((suggestion, i) => (
                            <li key={i}>{suggestion}</li>
                          ))}
                        </ul>
                      )}
                      {!passwordStrength.isStrong && password.length < 10 && (
                        <p className="text-orange-600 mt-1">Password must be at least 10 characters</p>
                      )}
                    </div>
                  )}
                </div>
                
                {formErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                )}
              </div>
              
              <div className="pt-4">
                <p className="text-center font-medium text-black mb-4">Turn Website Visitors Into Leads Instantly</p>
                <Button type="submit" className="w-full bg-black hover:bg-black/90 h-12 text-base" disabled={loading}>
                  {loading ? "Creating account..." : "Sign Up"}
                </Button>
              </div>
              
              <p className="text-xs text-center text-gray-500 mt-4">
                By signing up, you agree to our <Link to="/terms" className="text-black hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-black hover:underline">Privacy Policy</Link>
              </p>
              
              <div className="flex items-center justify-center space-x-2 mt-6">
                <p className="text-sm text-black">
                  Already have an account?
                </p>
                <Link to="/login" className="text-black font-medium hover:underline text-sm">
                  Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>;
};

export default SignUp;
